import { Application, PersistedApplication } from '@/types/application';
import { Connection } from '@/types/connection';
import { Detail } from '@/types/detail';
import { Block, Metadata, Port, PortOption } from '@/types/metadata/metadata';
import { getHighestConnectionId } from '@/util/application';
import { stripEmptyDetails } from '@/util/blocks';
import { isConfigurationEnum, isNullOrUndefined, isNumber, isXYPosition } from '@/util/typeGuards';

// Adds a connection for every metadata port the given connections are missing.
const fillMissingMetaPorts = (connections: Connection[], metaPorts: Port[]): Connection[] => {
  const newConnections = structuredClone(connections);
  const missingMetaPorts = metaPorts?.filter(
    (port) => !newConnections.find((connection) => connection.action === port.action)
  );

  missingMetaPorts?.forEach((missingMetaPort) => {
    const missingConnection: Connection = {
      id: getHighestConnectionId(newConnections) + 1,
      action: missingMetaPort.action,
      dst: null,
      label: missingMetaPort.label
    };
    newConnections.push(missingConnection);
  });

  return newConnections;
};

/**
 * Falls back to the first selectable option when the saved index is gone from the metadata or points
 * at a 'nothing' option. findIndex gives -1 when every option is 'nothing', hence the clamp.
 */
const usablePortOptionIndex = (outputPorts: PortOption[], savedIndex: number): number => {
  const saved = outputPorts[savedIndex];
  if (saved && saved.type !== 'nothing') return savedIndex;

  return Math.max(
    outputPorts.findIndex((port) => port.type !== 'nothing'),
    0
  );
};

/**
 * Prefills the connections, details and port option of a single block. Returns a new application
 * with fresh `blocks` references instead of mutating the one passed in, which is what lets the
 * effects keyed on those references re-run.
 */
export const prefillConnection = (
  application: Application,
  metadata: Metadata,
  id: string,
  metadataBlock?: Block
): Application => {
  const details: Detail = { ...application.blocks.details[id] };

  let connections = ensureConnectionsHasIds(application.blocks.connections[id] ?? []);
  let portoption = application.blocks.portoptions[id];

  const type = application.blocks.nodes[id]?.type;
  const metaBlock = metadataBlock || metadata.blocks.find((block) => block.type === type);

  if (metaBlock?.output_ports?.length) {
    const index = usablePortOptionIndex(metaBlock.output_ports, portoption?.index ?? 0);
    const chosenPort = metaBlock.output_ports[index];

    if (metaBlock.output_ports.length > 1) {
      portoption = { index, label: chosenPort.label };
    }

    connections = ensureMetaPortsInBottom(fillMissingMetaPorts(connections, chosenPort.ports), chosenPort.ports);

    metaBlock.configuration.forEach((config) => {
      if (config.default && isNullOrUndefined(details[config.value])) {
        details[config.value] = config.default;
      } else if (isConfigurationEnum(config)) {
        config.options.forEach((option) => {
          if (option.default && isNullOrUndefined(details[config.value])) {
            details[config.value] = option.value;
          }
        });
      }
    });
  }

  return {
    ...application,
    blocks: {
      ...application.blocks,
      connections: { ...application.blocks.connections, [id]: connections },
      details: { ...application.blocks.details, [id]: details },
      portoptions: portoption ? { ...application.blocks.portoptions, [id]: portoption } : application.blocks.portoptions
    }
  };
};

// Assigns a unique id to every connection missing one, leaving existing ids untouched.
export const ensureConnectionsHasIds = (connections: Connection[]): Connection[] => {
  let highestId = getHighestConnectionId(connections);
  return connections.map((connection) => {
    if (isNumber(connection.id)) {
      return connection;
    } else {
      highestId += 1;
      return { ...connection, id: highestId };
    }
  });
};

// Reorders connections so the ones backed by a metadata port sit at the bottom.
export const ensureMetaPortsInBottom = (connections: Connection[], metaPorts: Port[]) => {
  const onlyCustomConnections = connections.filter(
    (connection) => !metaPorts?.some((p) => p.action === connection.action)
  );
  const onlyMetaConnections = connections.filter((connection) =>
    metaPorts?.some((p) => p.action === connection.action)
  );

  return [...onlyCustomConnections, ...onlyMetaConnections];
};

// Prefills every block that has a connections entry, skipping entries whose block is gone.
const prefillConnections = (application: Application, metadata: Metadata): Application =>
  Object.keys(application.blocks.connections)
    .filter((id) => application.blocks.nodes[id])
    .reduce((prefilled, id) => prefillConnection(prefilled, metadata, id), application);

// Gives every block a details record and a layout with a position, defaulting whichever is missing.
const ensureAllBlocksHasCoordinates = (application: Application): Application => {
  application.blocks.layouts = application.blocks.layouts ?? {};

  application.subroutines.forEach((subroutine) => {
    subroutine.nodes.forEach((nodeId, index) => {
      if (!application.blocks.details[nodeId]) {
        application.blocks.details[nodeId] = {};
      }

      if (!isXYPosition(application.blocks.layouts[nodeId]?.position)) {
        application.blocks.layouts[nodeId] = {
          position: {
            x: index * 200,
            y: index * 0
          }
        };
      }
    });
  });

  return application;
};

// Puts all the data Block Engine Builder needs in place before an application is used.
export const preFillApplication = (baseApplication: Application, metadata: Metadata): Application => {
  const newApplication = structuredClone(baseApplication);
  const filled = prefillConnections(newApplication, metadata);
  const coordinated = ensureAllBlocksHasCoordinates(filled);
  return coordinated;
};

// Strips the builder-only connection ids and the empty details objects it keeps per block, before a save or export.
export const toPersistedApplication = (application: Application): PersistedApplication => ({
  ...application,
  blocks: {
    ...application.blocks,
    connections: Object.fromEntries(
      Object.entries(application.blocks.connections).map(([sourceId, connections]) => [
        sourceId,
        connections.map(({ id, ...persisted }) => persisted)
      ])
    ),
    details: stripEmptyDetails(application.blocks.details)
  }
});
